import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MailIcon from '@mui/icons-material//Mail';
import { Box, Button, styled, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { useSnackbar } from 'notistack';
import { Portal } from 'api';
import { useInformationRequestStatuses } from 'common/hooks/useInformationRequestStatuses';
import { useStore } from 'common/store';
import { Task } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { ExtraActionsMenu, Tab, TabsNavigation } from 'components';
import { GridSkeleton } from 'components/Grid';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { InformationContextValue } from 'context/InformationDashboard409AContext/types';
import { LinkVariant } from 'icons';
import useTabQueryParam from 'pages/ValuationsAllocation/hooks/useApproachQueryParam/useTabQueryParam';
import { useDocuments } from 'services/hooks';
import theme from 'theme';
import { CreateInformationRequestModal } from './CreateInformationRequestModal';
import CurrentRequestedDocument from './CurrentRequestedDocumentsAndQuestions/CurrentRequestedDocument';
import CurrentRequestedQuestions from './CurrentRequestedDocumentsAndQuestions/CurrentRequestedQuestions';
import TaskCompletionProgress from './CurrentRequestedDocumentsAndQuestions/TaskCompletionProgress';
import { InitialRequestProcessModal } from './InitialRequestProcessModal';
import { InformationProps } from './types';
import {
  filterCurrentInformationRequests,
  filterDocumentInformationRequests,
  filterQuestionnaireInformationRequests,
  getDocumentProgressValue,
  getQuestionnaireProgressValue,
} from './util/filterInformationRequests';
import { getRolePermissions } from './utils/rolePermissions';

const StyledGridContainer = styled(Grid)(() => ({
  margin: `-${theme.spacing(1.5)} !important`,
  width: `calc(100% + ${theme.spacing(3)}) !important`,
}));

const StyledGridHeader = styled(Grid)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
}));

const StyledTypographyTitle = styled(Typography)(() => ({
  fontSize: 32,
  textTransform: 'uppercase',
  marginBottom: 30,
  marginTop: 20,
  marginRight: 8,
}));

const StyledButtonOutlined = styled(Button)(() => ({
  marginRight: 20,
}));

const StyledButtonOutlinedSquareIcon = styled(Button)(() => ({
  marginRight: 20,
  padding: '6px 6px',
  minWidth: 0,
}));

const Information: (props: InformationProps) => React.JSX.Element = (props: InformationProps) => {
  const {
    currentCompany,
    currentMeasurementDateId,
    currentCompanyMeasurementDateId,
    is409a,
    measurementDates,
    selectedMeasurementDate,
  } = props;
  const [{ user, firmId }] = useStore() as unknown as UseStoreValues;
  const userRole = Array.isArray(user?.firms_permissions)
    ? user?.firms_permissions?.find(permission => permission.id === firmId)?.user_firm_role
    : undefined;

  const [isInfoSetupModalOpen, setIsInfoSetupModalOpen] = useState(false);
  const [isTaskPreparationModalOpen, setIsTaskPreparationModalOpen] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [taskPortalLinkData, setTaskPortalLinkData] = useState<{ requestId: string; hmacToken: string }>();
  const {
    currentDocuments,
    getDocuments,
    downloadDocument,
    addFilesToFolder,
    updateSubFolder,
    featuresObjects,
    getFeaturesObjectsById,
    validateDocumentRequestToken,
    getPortalLinks,
  } = useDocuments(selectedMeasurementDate);
  const { companyData, setUpdateTaskList, handleGetCurrentInformationRequests, currentInformationRequests, hasLoaded } =
    useInformationRequestStatuses({
      currentCompany,
      currentMeasurementDateId,
      currentCompanyMeasurementDateId,
      is409a,
      reFetch: isInfoSetupModalOpen,
    });
  const { requested_documents: requestedDocuments, questions } = companyData;

  const companyId = useMemo(() => currentCompany?.id, [currentCompany]);

  const mdId = useMemo(() => currentMeasurementDateId, [currentMeasurementDateId]);

  useEffect(() => {
    if (currentMeasurementDateId) {
      getFeaturesObjectsById(currentMeasurementDateId);
    }
  }, [currentMeasurementDateId, getFeaturesObjectsById]);

  const refreshDocuments = useCallback(() => getDocuments(mdId, companyId), [getDocuments, mdId, companyId]);

  useEffect(() => {
    if (mdId && companyId) {
      refreshDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdId, companyId]);

  const { enqueueSnackbar } = useSnackbar();
  // if it is 409a, get company users, otherwise get firm users?
  const [portalLinks, setPortalLinks] = useState<Portal | undefined>();

  const updatePortalLinksAndTask = useCallback(async () => {
    if (!selectedMeasurementDate) {
      return;
    }
    const response = await getPortalLinks();
    setPortalLinks(response);
    if (response?.document_portal_link) {
      const portalDataArray = response.document_portal_link.split('/');
      // Both requestId and hmacToken are guaranteed to be present
      const requestId = portalDataArray.at(-2)!;
      const hmacToken = portalDataArray.at(-1)!;
      const tmpResponse = await validateDocumentRequestToken(requestId, hmacToken);
      setTaskPortalLinkData({
        requestId,
        hmacToken,
      });
      if (tmpResponse && typeof tmpResponse === 'object' && 'task' in tmpResponse) {
        setTask((tmpResponse as { task: Task }).task);
      }
    }
  }, [getPortalLinks, selectedMeasurementDate, validateDocumentRequestToken, setTaskPortalLinkData, setTask]);

  const copyToClipboard = useCallback(
    (link: string) => {
      navigator.clipboard.writeText(link);
      enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (selectedMeasurementDate) {
      updatePortalLinksAndTask();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurementDate]);

  const filteredInformationRequests = useMemo(
    () => filterCurrentInformationRequests(currentInformationRequests),
    [currentInformationRequests]
  );

  const documentsProgress = useMemo(() => {
    const progress = filterDocumentInformationRequests(filteredInformationRequests);
    return getDocumentProgressValue(progress);
  }, [filteredInformationRequests]);

  const questionnaireProgress = useMemo(() => {
    const progress = filterQuestionnaireInformationRequests(filteredInformationRequests);
    return getQuestionnaireProgressValue(progress);
  }, [filteredInformationRequests]);

  const documentsAndQuestionsProps = {
    companyId: currentCompany?.id,
    documents: requestedDocuments,
    questions,
    setUpdateTaskList,
  };

  const permissions = useMemo(() => getRolePermissions(userRole ?? 0), [userRole]);

  const optionsForExtraActionsMenu = useMemo(() => {
    if (!permissions.canEditSetup) {
      return [];
    }
    return [
      {
        label: 'Edit Company Questions and Documents',
        callback: () => setIsInfoSetupModalOpen(true),
        isActive: true,
      },
    ];
  }, [permissions.canEditSetup]);

  const providerValue = useMemo(
    () => ({
      handleGetCurrentInformationRequests,
      currentDocuments,
      currentMeasurementDateId,
      refreshDocuments,
      downloadDocument,
      addFilesToFolder,
      updateSubFolder,
      featuresObjects,
      measurementDates,
      task: task || ({} as Task),
      taskPortalLinkData,
      currentCompanyMeasurementDateId,
      permissions,
      updatePortalLinksAndTask,
      is409a,
    }),
    [
      featuresObjects,
      handleGetCurrentInformationRequests,
      currentDocuments,
      currentMeasurementDateId,
      refreshDocuments,
      downloadDocument,
      addFilesToFolder,
      updateSubFolder,
      measurementDates,
      task,
      taskPortalLinkData,
      currentCompanyMeasurementDateId,
      permissions,
      updatePortalLinksAndTask,
      is409a,
    ]
  );
  const { tabId } = useTabQueryParam();

  const tabs: Tab[] = useMemo(
    () => [
      {
        id: 'documents',
        label: 'Documents',
        content: <CurrentRequestedDocument currentInformationRequests={filteredInformationRequests} />,
      },
      {
        id: 'questions',
        label: 'Questions',
        content: <CurrentRequestedQuestions currentInformationRequests={filteredInformationRequests} />,
      },
    ],
    [filteredInformationRequests]
  );

  const selectedTab = useMemo(() => (tabs.find(tab => tab.id === tabId) || tabs[0]).label, [tabId, tabs]);

  if (!hasLoaded) {
    return <GridSkeleton />;
  }

  return (
    <InformationDashboard409AContext.Provider value={providerValue as InformationContextValue}>
      <StyledGridContainer container spacing={2} id="tabs-navigation-container">
        <TabsNavigation tabs={tabs} sidePanelOpen={is409a}>
          <StyledGridHeader>
            <Grid>
              <br />
              <Box position="relative" display="inline-flex">
                <StyledTypographyTitle>{selectedTab}</StyledTypographyTitle>
                {permissions?.canUseInfoRequestLedger && (
                  <TaskCompletionProgress
                    {...(tabId === 'documents' || !tabId ? documentsProgress : questionnaireProgress)}
                  />
                )}
              </Box>
            </Grid>
            <Grid>
              {permissions.canSendInfoRequest && (
                <StyledButtonOutlined
                  variant="contained"
                  color="primary"
                  onClick={() => setIsTaskPreparationModalOpen(true)}
                  startIcon={<MailIcon />}>
                  {currentInformationRequests?.length ? 'UPDATE INFO REQUEST' : 'NEW INFO REQUEST'}
                </StyledButtonOutlined>
              )}
              {permissions.canCopyPortalLink && currentInformationRequests?.length > 0 && (
                <StyledButtonOutlinedSquareIcon
                  variant="contained"
                  color="primary"
                  onClick={() => copyToClipboard(portalLinks?.document_portal_link ?? '')}>
                  <LinkVariant />
                  &nbsp;
                </StyledButtonOutlinedSquareIcon>
              )}
              {optionsForExtraActionsMenu.length > 0 && (
                <ExtraActionsMenu options={optionsForExtraActionsMenu} dotsColor={theme.palette.grey[500]} />
              )}
            </Grid>
          </StyledGridHeader>
          <CreateInformationRequestModal
            isOpen={isTaskPreparationModalOpen}
            setIsOpen={() => setIsTaskPreparationModalOpen(false)}
            currentMeasurementDateId={currentMeasurementDateId}
            currentCompanyMeasurementDateId={currentCompanyMeasurementDateId}
            is409a={is409a}
            handleGetCurrentInformationRequests={handleGetCurrentInformationRequests}
            currentCompanyId={currentCompany?.id}
          />
          <InitialRequestProcessModal
            {...documentsAndQuestionsProps}
            isOpen={isInfoSetupModalOpen}
            setIsOpen={setIsInfoSetupModalOpen}
          />
        </TabsNavigation>
      </StyledGridContainer>
    </InformationDashboard409AContext.Provider>
  );
};

export default Information;
